/**
 * Add Student Service
 *
 * Service for interacting with the Add Student NGXS state.
 */

import { Injectable, inject } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  InitializeAddStudent,
  SetStudentField,
  UpdateStudentFields,
  ResetAddStudent,
  SetStudentData,
  ClearAddStudent,
} from './add-student.actions';
import { AddStudentState, AddStudentStateModel } from './add-student.state';
import { Student, ActivityType } from 'src/app/models/schema';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { AuthService } from 'src/app/store/auth';
import { ActiveTeamService } from 'src/app/rxjs-store/account/active-team';
import { TeamActivityService } from 'src/app/rxjs-store/account/team-activity';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { AddStudent } from 'src/app/rxjs-store/admin/students/students.actions';
import { UtilsService } from 'src/app/services/utils.service';

@Injectable({
  providedIn: 'root',
})
export class AddStudentService {
  /**
   * Observable of the entire add student state
   */
  @Select(AddStudentState.getState) state$!: Observable<AddStudentStateModel>;

  /**
   * Observable of student ID
   */
  @Select(AddStudentState.getStudentId) studentId$!: Observable<string | undefined>;

  /**
   * Observable of student first name
   */
  @Select(AddStudentState.getFirstName) firstName$!: Observable<string | undefined>;

  /**
   * Observable of student last name
   */
  @Select(AddStudentState.getLastName) lastName$!: Observable<string | undefined>;

  /**
   * Observable of student grade
   */
  @Select(AddStudentState.getGrade) grade$!: Observable<string | undefined>;

  /**
   * Observable of date of birth
   */
  @Select(AddStudentState.getDateOfBirth) dateOfBirth$!: Observable<number | undefined>;

  /**
   * Observable of student school
   */
  @Select(AddStudentState.getSchool) school$!: Observable<string | undefined>;

  /**
   * Observable of student race
   */
  @Select(AddStudentState.getRace) race$!: Observable<string | undefined>;

  /**
   * Observable of student gender
   */
  @Select(AddStudentState.getGender) gender$!: Observable<'Male' | 'Female' | 'Nonbinary' | undefined>;

  /**
   * Observable of disability type
   */
  @Select(AddStudentState.getDisabilityType) disabilityType$!: Observable<string[] | undefined>;

  /**
   * Observable of english learner status
   */
  @Select(AddStudentState.getEnglishLearner) englishLearner$!: Observable<boolean | undefined>;

  /**
   * Observable of areas of need
   */
  @Select(AddStudentState.getAreasOfNeed) areasOfNeed$!: Observable<string[]>;

  /**
   * Observable of areas of strength
   */
  @Select(AddStudentState.getAreasOfStrength) areasOfStrength$!: Observable<string[]>;

  /**
   * Observable of created at timestamp
   */
  @Select(AddStudentState.getCreatedAt) createdAt$!: Observable<number | undefined>;

  /**
   * Observable of created by user ID
   */
  @Select(AddStudentState.getCreatedBy) createdBy$!: Observable<string | undefined>;

  /**
   * Observable of student data
   */
  @Select(AddStudentState.getStudentData) studentData$!: Observable<Partial<Student>>;

  /**
   * Observable of whether form has data
   */
  @Select(AddStudentState.hasData) hasData$!: Observable<boolean>;

  private firestoreService: FirestoreService = inject(FirestoreService);
  private authService: AuthService = inject(AuthService);
  private activeTeamService: ActiveTeamService = inject(ActiveTeamService);
  private teamActivityService: TeamActivityService = inject(TeamActivityService);
  private utilsService: UtilsService = inject(UtilsService);

  constructor(private store: Store) {}

  /**
   * Get the current state synchronously
   */
  getState(): AddStudentStateModel {
    return this.store.selectSnapshot(AddStudentState.getState);
  }

  /**
   * Get student ID
   */
  getStudentId(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getStudentId);
  }

  /**
   * Get student first name
   */
  getFirstName(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getFirstName);
  }

  /**
   * Get student last name
   */
  getLastName(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getLastName);
  }

  /**
   * Get student grade
   */
  getGrade(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getGrade);
  }

  /**
   * Get date of birth
   */
  getDateOfBirth(): number | undefined {
    return this.store.selectSnapshot(AddStudentState.getDateOfBirth);
  }

  /**
   * Get student school
   */
  getSchool(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getSchool);
  }

  /**
   * Get student race
   */
  getRace(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getRace);
  }

  /**
   * Get student gender
   */
  getGender(): 'Male' | 'Female' | 'Nonbinary' | undefined {
    return this.store.selectSnapshot(AddStudentState.getGender);
  }

  /**
   * Get disability type
   */
  getDisabilityType(): string[] | undefined {
    return this.store.selectSnapshot(AddStudentState.getDisabilityType);
  }

  /**
   * Get english learner status
   */
  getEnglishLearner(): boolean | undefined {
    return this.store.selectSnapshot(AddStudentState.getEnglishLearner);
  }

  /**
   * Get areas of need
   */
  getAreasOfNeed(): string[] {
    return this.store.selectSnapshot(AddStudentState.getAreasOfNeed);
  }

  /**
   * Get areas of strength
   */
  getAreasOfStrength(): string[] {
    return this.store.selectSnapshot(AddStudentState.getAreasOfStrength);
  }

  /**
   * Get created at timestamp
   */
  getCreatedAt(): number | undefined {
    return this.store.selectSnapshot(AddStudentState.getCreatedAt);
  }

  /**
   * Get created by user ID
   */
  getCreatedBy(): string | undefined {
    return this.store.selectSnapshot(AddStudentState.getCreatedBy);
  }

  /**
   * Get student data
   */
  getStudentData(): Partial<Student> {
    return this.store.selectSnapshot(AddStudentState.getStudentData);
  }

  /**
   * Check if form has data
   */
  hasData(): boolean {
    return this.store.selectSnapshot(AddStudentState.hasData);
  }

  /**
   * Initialize add student form
   */
  async initialize(): Promise<void> {
    return this.store.dispatch(new InitializeAddStudent()).toPromise();
  }

  /**
   * Set a student field
   */
  async setField(field: keyof Student, value: any): Promise<void> {
    return this.store.dispatch(new SetStudentField(field, value)).toPromise();
  }

  /**
   * Update multiple student fields
   */
  async updateFields(updates: Partial<Student>): Promise<void> {
    return this.store.dispatch(new UpdateStudentFields(updates)).toPromise();
  }

  /**
   * Reset add student form
   */
  async reset(): Promise<void> {
    return this.store.dispatch(new ResetAddStudent()).toPromise();
  }

  /**
   * Set student data (e.g., when editing)
   */
  async setStudentData(student: Partial<Student>): Promise<void> {
    return this.store.dispatch(new SetStudentData(student)).toPromise();
  }

  /**
   * Clear add student form
   */
  async clear(): Promise<void> {
    return this.store.dispatch(new ClearAddStudent()).toPromise();
  }

  /**
   * Convert undefined values to null for Firestore compatibility
   * Firestore doesn't support undefined values, so we convert them to null
   */
  private convertUndefinedToNull<T extends Record<string, any>>(obj: T): T {
    const result: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value === undefined) {
          result[key] = null;
        } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          // Recursively handle nested objects
          result[key] = this.convertUndefinedToNull(value);
        } else {
          result[key] = value;
        }
      }
    }
    return result as T;
  }

  /**
   * Create a new student in the active team/partner
   *
   * Creates a new student profile in Firestore with proper metadata.
   *
   * @returns Promise that resolves to the created student ID
   * @throws Error if the operation fails
   *
   * @example
   * try {
   *   const studentId = await this.addStudentService.createStudent();
   *   console.log('Student created:', studentId);
   * } catch (error) {
   *   console.error('Failed to create student:', error);
   * }
   */
  async createStudent(): Promise<string> {
    try {
      // Get the form data
      const studentData = this.getStudentData();

      // Validate required fields
      if (!studentData.firstName || !studentData.lastName) {
        throw new Error('First name and last name are required');
      }
      if (!studentData.grade) {
        throw new Error('Grade is required');
      }

      // Get active team info
      const activeTeamId = this.activeTeamService.getActiveTeamId();
      if (!activeTeamId) {
        throw new Error('No active team selected');
      }

      const activeTeam = this.activeTeamService.getActiveTeam();
      const partnerId = activeTeam?.id;
      if (!partnerId) {
        throw new Error('No partner ID found for active team');
      }

      // Get current user info
      const currentUserEmail = this.authService.getUserEmail();
      const now = Date.now();

      // Generate student ID
      const generatedStudentId = this.utilsService.generateId();

      // Generate search name (normalized firstName + lastName)
      const searchName = `${studentData.firstName}${studentData.lastName}`
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

      // Prepare student data for Firestore
      const studentToCreate: Partial<Student> = {
        id: generatedStudentId,
        studentId: studentData.studentId || generatedStudentId,
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        searchName,
        grade: studentData.grade,
        dateOfBirth: studentData.dateOfBirth,
        school: studentData.school,
        race: studentData.race,
        gender: studentData.gender,
        disabilityType: studentData.disabilityType,
        englishLearner: studentData.englishLearner || false,
        areasOfNeed: studentData.areasOfNeed || [],
        areasOfStrength: studentData.areasOfStrength || [],
        createdAt: now,
        createdBy: currentUserEmail || 'system',
        updatedAt: now,
        updatedBy: currentUserEmail || 'system',
      };

      // Convert undefined to null for Firestore
      const cleanedStudent = this.convertUndefinedToNull(studentToCreate);

      // Validate current user email
      if (!currentUserEmail) {
        console.error('[AddStudentService] No current user email available');
        throw new Error('Current user email is required');
      }

      // Get current user's profile
      const profile = this.store.selectSnapshot(ProfileState.profile);
      const currentCaseload = profile?.caseload || [];
      const userProfilePath = `partners/${partnerId}/users/${currentUserEmail.toLowerCase()}`;
      
      console.log('[AddStudentService] Creating student with transaction');
      console.log('[AddStudentService] Current user email:', currentUserEmail);
      console.log('[AddStudentService] Current caseload:', currentCaseload);
      console.log('[AddStudentService] New student ID:', generatedStudentId);

      // Use a Firestore transaction to atomically:
      // 1. Create the student document
      // 2. Update the user's caseload
      await this.firestoreService.runTransaction(async (transaction) => {
        const studentDocPath = `partners/${partnerId}/students/${generatedStudentId}`;
        const studentRef = this.firestoreService.doc(studentDocPath);
        
        // Set the student document
        transaction.set(studentRef, cleanedStudent);
        console.log('[AddStudentService] Transaction: Setting student document at', studentDocPath);
        
        // Add student to user's caseload if not already there
        if (!currentCaseload.includes(generatedStudentId)) {
          const updatedCaseload = [...currentCaseload, generatedStudentId];
          const userProfileRef = this.firestoreService.doc(userProfilePath);
          
          // Use set with merge:true instead of update to handle cases where document might not exist
          transaction.set(userProfileRef, {
            caseload: updatedCaseload,
            updatedAt: now,
            updatedBy: currentUserEmail,
          }, { merge: true });
          
          console.log('[AddStudentService] Transaction: Updating caseload at', userProfilePath);
          console.log('[AddStudentService] Transaction: New caseload will be', updatedCaseload);
        } else {
          console.log('[AddStudentService] Transaction: Skipping caseload update (student already in caseload)');
        }
      });
      
      console.log('[AddStudentService] Transaction completed successfully');

      // Add student to local Students state immediately
      this.store.dispatch(new AddStudent(cleanedStudent as Student));

      // Log activity
      await this.teamActivityService.addActivity(partnerId, {
        uid: this.authService.getUserId() || '',
        email: currentUserEmail || 'system',
        category: 'students' as any,
        activityType: ActivityType.CREATED_STUDENT,
        details: {
          personalNarrative: `You added student ${studentData.firstName} ${studentData.lastName} (Grade ${studentData.grade})`,
          teamNarrative: `${currentUserEmail} added student ${studentData.firstName} ${studentData.lastName} (Grade ${studentData.grade})`,
          studentId: generatedStudentId,
          studentName: `${studentData.firstName} ${studentData.lastName}`,
          grade: studentData.grade,
        },
      });

      console.log('✅ Student created successfully:', generatedStudentId);

      return generatedStudentId;
    } catch (error: any) {
      console.error('[AddStudentService] Error creating student:', error);
      throw error;
    }
  }
}

